/* eslint-disable import/extensions */
/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */
/**
 *
 * SettlementBankDetails
 *
 */

import React, { memo, useContext, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { compose } from 'redux';

import { getIcon } from 'payments-icons-library';
import {
  Grid,
  Column,
  Text,
  Conditional,
  Popup,
  Button,
  Icon,
  Space,
  TwoFactorAuthModal,
  toast,
} from '@cashfree-intl/coherent';

import { useInjectSaga } from 'utils/injectSaga';
import { useInjectReducer } from 'utils/injectReducer';
import { roundOffNumber, useQuery } from 'utils/common';

import { Image } from 'components/Image';

// Constants
import { CURRENCY_SYMBOL, PGDB_LOCK_SETTLEMENT_ACCOUNT, SOURCE_TYPE, TWO_FA_TYPE, user } from 'utils/constants';
import { OB_STATUS as STATUS } from 'constants/enums';
import { BANK_ACCOUNT_NAMES_ICON, MODALS, initialAuthInfo, fieldFormatMapper, INDIVIDUAL_ORG_TYPES, mapStateToProps, mapDispatchToProps, TICKET_TYPE, REG_KEYS_TO_FETCH } from './constants';

import reducer from './reducer';
import saga from './saga';


// Components
import Loader from '../../components/Loader';
import CustomIcons from '../../components/CustomIcons';
import RefundRechargeModal from './components/RefundRechargeModal';
import CancelledChequeModal from './components/CancelledChequeModal';
import ChangeAccountModal from './components/ChangeAccountModal';
import UpdatePayoutsModal from './components/UpdatePayoutsModal';
import EditChequeModal from './components/EditChequeModal';
import ReviewModal from './components/ReviewModal';
import AlternateIdProof from 'components/AlternateIdProof';

import OnboardingProvider from 'providers/OnboardingProvider';
import { useOnboardingData } from 'hooks/useOnboardingData';
import TwoFAProvider from 'providers/TwoFAProvider';
import { useTwoFAData } from 'hooks/useTwoFAData';
import { getInfoText } from 'utils/modal';

// Types
import { BankAccountType } from './types';

// Styled
import { StyledAccountHeaderIcon } from './styled';
import { MerchantContext } from 'contexts/MerchantContext';

export function SettlementBankDetails(props) {
  useInjectReducer({ key: 'settlementBankDetails', reducer });
  useInjectSaga({ key: 'settlementBankDetails', saga, mode: '' });

  const { featureToggles = {} } = useContext(MerchantContext);
  const lockSettlementAccount = PGDB_LOCK_SETTLEMENT_ACCOUNT in featureToggles && featureToggles[PGDB_LOCK_SETTLEMENT_ACCOUNT];

  const [open, resetModal] = useState('');
  const [newBankAccountData, setNewBankAccountData] = useState({
    accountNumber: "",
    ifscCode: "",
    isPayoutSelected: false,
    deleted: false,
    payoutOptions: []
  });
  const query = useQuery();
  const env = query.get('env');
  const { fetchRegDetails, fetchMerchantDocs, data: onboardingData, updateDocDetails } = useOnboardingData();
  const { activeMethodData, confirm2FA, open2FA, close2FA, sendOtp, is2FAVerified, modeActive } = useTwoFAData();
  const openChangeAccountModal = () => {
    fetchPayoutIds();
    resetModal(MODALS.CHANGE_ACCOUNT)
  }
  const openEditChequeModal = () => resetModal(MODALS.EDIT_CHEQUE)
  const onConfirmOtp = confirm2FA({
    cb: openChangeAccountModal,
  })

  const {
    enableLoader,
    getBankAccounts,
    getRefundRecharge,
    twoFactorAuthentication,
    resetTwoFactorAuthentication,
    resetRefundRecharge,
    resetData,
    cancelNewBankAccount,
    verifyBankAccount,
    enableModalLoader,
    disableModalLoader,
    createSelfServeTicket,
    getPayoutBankIds,
    userType,
    settlementBankDetails: {
      loaders,
      data,
      rechargeDetails,
      refundRechargeData,
    },
  } = props;

  const { isPageLoading, isModalLoading } = loaders || {};
  const activeAccount = data?.filter(bank => bank.isActive === 1)[0];
  const isMerchantOwner = userType === user.MERCHANT_OWNER;

  const {
    loader,
    callRefundRechargeApi,
    startDate,
    endDate,
    pageNo,
    pageSize,
  } = refundRechargeData;

  const getPageData = () => {
    enableLoader();
    getBankAccounts({
      cbs: [() => fetchRegDetails({
        regKeys: REG_KEYS_TO_FETCH
      }), () => fetchMerchantDocs({})],
    });
  };

  useEffect(() => getPageData(), [env]);

  useEffect(() => {
    if (twoFactorAuthentication) {
      getPageData();
      resetTwoFactorAuthentication(false);
    }
    return () => resetData();
  }, [twoFactorAuthentication]);

  const fetchPayoutIds = () => {
    enableModalLoader();
    getPayoutBankIds({
      cb: data => {
        setNewBankAccountData(prev => ({ ...prev, payoutOptions: data }));
      },
      finalCb: () => {
        disableModalLoader();
      },
    });
  };

  const openModal = () => {
    const options = { startDate, endDate, pageNo: 0, pageSize };
    enableLoader();
    resetRefundRecharge();
    getRefundRecharge({
      options,
      cb: () => resetModal('REFUND_RECHARGE'),
    });
  };

  const closeRefundModal = () => {
    resetRefundRecharge();
    resetModal('');
  };
  const closeModal = () => resetModal('');

  const getUpdatedData = () => {
    if (callRefundRechargeApi && !loader) {
      const options = { startDate, endDate, pageNo: pageNo + 1, pageSize };
      enableLoader('REFUND_RECHARGE');
      getRefundRecharge({
        options,
      });
    }
  };

  const onChangeAccount = (data) => {
    if (newBankAccountData.payoutOptions.length > 0) {
      resetModal(MODALS.UPDATE_PAYOUTS)
    } else {
      onUpdatePayouts([], false, data.ifscCode, data.accountNumber)
    }
    setNewBankAccountData(prev => ({ ...prev, ...data }))
  }

  const onUpdatePayouts = (payoutAccounts: number[], isPayoutSelected: boolean, ifsc?: string, bankAccount?: string) => {
    enableModalLoader();
    verifyBankAccount({
      payload: {
        ifsc: ifsc ?? newBankAccountData.ifscCode,
        bankAccount: bankAccount ?? newBankAccountData.accountNumber,
        sourceType: SOURCE_TYPE.DASHBOARD,
        payoutAccounts
      },
      cb: () => {
        setNewBankAccountData(prev => ({ ...prev, isPayoutSelected: isPayoutSelected && payoutAccounts.length > 0 }))
        resetModal(MODALS.REVIEW_MODAL)
      },
      errorCb: (errorMeta) => {
        const { message, data } = errorMeta || {}
        if (data?.valid && !data?.nameMatchSuccessful) {
          resetModal(MODALS.CANCELLED_CHEQUE);
        } else toast.error(message);
      },
      finalCb: disableModalLoader,
    })
  }

  const handleUploadChange = (payload: any, field: string) => {
    const { file } = payload;
    if (file) {
      const obj: any = {};
      const fileSize = file.size / 1024 / 1024; // in MB

      if (fileSize > 20) {
        toast.error(
          `Error while uploading file. File size should be less than 20 MB`,
        );
      } else {
        const fileName = file.name;
        const fieldName = field;

        obj[fieldName] = { file, fileName };
        enableModalLoader();
        updateDocDetails({
          payload: { fieldFormatMapper, data: obj },
          finalCb: disableModalLoader,
          errorCb: (err) => {
            toast.error(err?.errorMeta?.message)
          }
        });
      }
    }
  };

  const onBankAccountChange = () => {
    open2FA({
      cb: openChangeAccountModal
    })
  }

  const onCancelAccount = () => {
    enableLoader()
    cancelNewBankAccount({
      cb: () => {
        setNewBankAccountData(prev => ({ ...prev, deleted: true }))
      }
    })
  }

  const onCancelledChequeSubmit = () => {
    enableModalLoader()
    createSelfServeTicket({
      payload: {
        ticketType: TICKET_TYPE.NAME_MATCH_FAIL,
        accountNumber: newBankAccountData.accountNumber,
        ifsc: newBankAccountData.ifscCode
      },
      finalCb: disableModalLoader,
      cb: () => {
        resetModal('')
        getPageData()
      }
    })

  }
  const { accountHolder_selfServe, accountNumber_selfServe,
    ifscCode_selfServe, bankName_selfServe, orgType, ownerPanName, companyPanName } = onboardingData.regDetails || {};

  const { cheque_selfServe } = onboardingData.merchantDocs || {}

  const unnapprovedStatuses = [STATUS.REJECTED, STATUS.UNAPPROVED]

  const newAccount = unnapprovedStatuses.includes(accountNumber_selfServe?.isApproved) ? {
    accountHolder: accountHolder_selfServe?.regVal,
    accountNumber: accountNumber_selfServe?.regVal,
    ifscCode: ifscCode_selfServe?.regVal,
    bankName: bankName_selfServe?.regVal,
    isActive: accountNumber_selfServe?.isApproved,
    cheque: cheque_selfServe,
    rejectionReasons: accountNumber_selfServe?.rejectionReasons
  } : undefined

  const inActiveAccounts = data.filter(bank => !bank.isActive);
  const isIndividualOrProprietorship =
    orgType &&
    INDIVIDUAL_ORG_TYPES.includes(orgType.regVal);
  const accountName = isIndividualOrProprietorship ? ownerPanName?.regVal : companyPanName?.regVal;

  const renderBankAccount = (bankAccount: BankAccountType, rechargeAccount = false, newAccountPresent = false, isNewAccount = false) => {
    const isAccountUnapproved = bankAccount.isActive === STATUS.UNAPPROVED
    const isActiveAccount = !rechargeAccount && !isNewAccount && bankAccount.isActive === STATUS.APPROVED
    const isNewAccountRejected = isNewAccount && bankAccount.isActive === STATUS.REJECTED;
    const showChangeAccount = isMerchantOwner && ((!newAccountPresent && isActiveAccount) || isNewAccountRejected);

    const { icon_name: iconName, icon_url: iconUrl } = getIcon(
      BANK_ACCOUNT_NAMES_ICON[bankAccount.bankName?.toUpperCase()],
      'svg',
    );

    return (<div
      className={`card bank-details-card ${!bankAccount.isActive && !rechargeAccount ? 'inactive-card' : ''
        }`}
      key={bankAccount.accountNumber}
    >
      <Conditional if={unnapprovedStatuses.includes(bankAccount.isActive) && isNewAccount}>
        <Text variant="b14" color={isAccountUnapproved ? 'warning' : 'danger'} className={`card-header ${isAccountUnapproved ? 'warning-bg' : 'danger-bg'}`}>
          {isAccountUnapproved ? 'Usually takes up to 48 hours' : bankAccount.rejectionReasons?.[0] ?? 'Bank Details Rejected'}
        </Text>
      </Conditional>
      <Grid columns={2} className='card-body'>
        <Column>
          <div className="content-header">Account Name</div>
          <div className="content-description">
            {bankAccount.accountHolder}{' '}
          </div>
        </Column>
        <Column>
          <div className="content-header">
            Bank Name
            {rechargeAccount && (
              <>
                <span className="view-details" onClick={openModal} data-event-name='Link_ViewLedger'>
                  View Ledger
                </span>
                <RefundRechargeModal
                  open={open === 'REFUND_RECHARGE'}
                  closeModal={closeRefundModal}
                  data={refundRechargeData.data}
                  getUpdatedData={getUpdatedData}
                  loader={loader}
                />
              </>
            )}
          </div>
          <div className="content-description">
            <Conditional if={iconName !== 'default'}>
              <Popup
                content={bankAccount.bankName}
                trigger={
                  <span className="pointer">
                    <Image src={iconUrl} />
                  </span>
                }
              />
            </Conditional>
            <Conditional if={iconName === 'default'}>
              {bankAccount.bankName || 'NA'}
            </Conditional>
          </div>
        </Column>
        <Column>
          <div className="content-header">Account Number</div>
          <div className="content-description">
            {bankAccount.accountNumber}{' '}
          </div>
        </Column>
        <Column>
          <div className="content-header">IFSC</div>
          <div className="content-description">{bankAccount.ifscCode} </div>
        </Column>
        {bankAccount.rechargeamount && (
          <Column>
            <div className="content-header">Minimum Recharge Value</div>
            <div className="content-description">
              {CURRENCY_SYMBOL.INR} {roundOffNumber(bankAccount.rechargeamount)}{' '}
            </div>
          </Column>
        )}

        <Conditional if={newAccountPresent && isActiveAccount}>
          <Column stretched width={16}>
            <Space alignItems='center'>
              <Icon name='info-small' width={16} height={16} />
              <Text variant="b12" color="bodyLight" className='ml-1'>Your will receive settlements in this bank account till the new bank account is verified </Text>
            </Space>
          </Column>
        </Conditional>
        <Conditional if={!!(isNewAccount && bankAccount?.cheque?.docName)}>
          <Column stretched width={16}>
            <AlternateIdProof docName='Cancelled Cheque'
              hasNotSubmittedAlternateDoc={false}
              isAlternateDocRejected={bankAccount.cheque?.isApproved === STATUS.REJECTED}
              onClick={openEditChequeModal}
              alternateDocHeader='Cancelled Cheque' />
          </Column>
        </Conditional>
        <Conditional if={showChangeAccount}>
          <Column>
            <Popup
              content="Updating bank account is currently locked for you. Please contact care@cashfree.com for assistance."
              disabled={!lockSettlementAccount}
              position="left center"
              trigger={<span className={lockSettlementAccount ? '' : 'pointer'}>
                <Button disabled={lockSettlementAccount} onClick={onBankAccountChange} primary>
                  {isActiveAccount ? 'Change Bank Account' : 'Review & Fix'}
                </Button>
              </span>
              }
            />
          </Column>
        </Conditional>
      </Grid>
    </div>)
  };

  return (
    <div className="settlement-bank-details-wrapper">
      <Loader active={isPageLoading} />
      <Conditional if={open === MODALS.CANCELLED_CHEQUE}>
        <CancelledChequeModal onClose={closeModal} handleUploadChange={handleUploadChange}
          accountName={accountName} chequeDetails={cheque_selfServe}
          loading={isModalLoading} onSubmit={onCancelledChequeSubmit}
        />
      </Conditional>
      <Conditional if={open === MODALS.UPDATE_PAYOUTS}>
        <UpdatePayoutsModal onClose={closeModal} onSubmit={onUpdatePayouts} isModalLoading={isModalLoading} payoutBankAccounts={newBankAccountData?.payoutOptions} />
      </Conditional>
      <Conditional if={open === MODALS.REVIEW_MODAL}>
        <ReviewModal isPayoutsUpdated={newBankAccountData?.isPayoutSelected} onClose={closeModal} />
      </Conditional>
      <Conditional if={open === MODALS.CHANGE_ACCOUNT}>
        <ChangeAccountModal
          accountName={accountName}
          onClose={closeModal}
          onSubmit={onChangeAccount}
          accountNumberDetails={accountNumber_selfServe}
          ifsCodeDetails={ifscCode_selfServe}
        />
      </Conditional>
      <Conditional if={open === MODALS.EDIT_CHEQUE}>
        <EditChequeModal
          accountName={accountName}
          onClose={closeModal}
          chequeDetails={cheque_selfServe}
          handleUploadChange={handleUploadChange}
          loading={isModalLoading}
          disableEdit={accountNumber_selfServe?.isApproved !== STATUS.REJECTED}
        />
      </Conditional>
      <div className="description-container">
        <div className="text-light">
          You will receive your settlements in the bank accounts below. Write to{' '}
          <a href="mailto:care@cashfree.com">care@cashfree.com</a> or contact
          your Account Manager to update the bank accounts
        </div>
      </div>
      {activeAccount && (
        <div className="bank-details-row">
          <div className="account-type"><span>Bank Account</span><Text variant="b14" color='success' className='status success-bg ml-2'>
            Active
          </Text></div>
          {renderBankAccount(activeAccount, false, !!newAccount)}
        </div>
      )}
      {newAccount && newAccount.isActive !== STATUS.DELETED && !newBankAccountData.deleted && (
        <div className="bank-details-row">
          <Space className="account-type" alignItems='center'>
            <span>New Bank Account</span>
            <Conditional if={newAccount.isActive === STATUS.UNAPPROVED}>
              <Text variant='b14' color='warning' className='status warning-bg ml-2'>Under Review</Text>
            </Conditional>
            <Conditional if={newAccount.isActive === STATUS.REJECTED}>
              <Text variant='b14' color='danger' className='status danger-bg ml-2'>Rejected</Text>
              <StyledAccountHeaderIcon name='delete' onClick={onCancelAccount} />
            </Conditional>
          </Space>
          {renderBankAccount(newAccount, false, true, true)}
        </div>
      )
      }
      {rechargeDetails && Object.keys(rechargeDetails).length > 0 && (
        <div className="bank-details-row">
          <div className="account-type">Recharge Account</div>
          {renderBankAccount(rechargeDetails, true)}
        </div>
      )}
      {inActiveAccounts.length > 0 && (
        <div className="bank-details-row">
          <div className="account-type">Inactive Account</div>
          {inActiveAccounts.map(bankAccount => renderBankAccount(bankAccount))}
        </div>
      )}
      <Conditional if={!is2FAVerified}>
        <>
          <Conditional if={modeActive === TWO_FA_TYPE.G2FA}>
            <TwoFactorAuthModal
              open
              type={TWO_FA_TYPE.G2FA}
              infoLabel={
                <>
                  Enter the 6-digit authentication code from your authenticator
                  app
                </>
              }
              authInfo={initialAuthInfo}
              onConfirm={onConfirmOtp}
              onClose={close2FA}
              onResend={async () => { }}
            />
          </Conditional>
          <Conditional if={modeActive === TWO_FA_TYPE.OTP}>
            <TwoFactorAuthModal
              open
              type={TWO_FA_TYPE.OTP}
              infoLabel={getInfoText(activeMethodData)}
              authInfo={initialAuthInfo}
              onConfirm={onConfirmOtp}
              onClose={close2FA}
              onResend={sendOtp}
            />
          </Conditional>
        </>
      </Conditional>
    </div>
  );
}
SettlementBankDetails.propTypes = {
  enableLoader: PropTypes.func.isRequired,
  getBankAccounts: PropTypes.func.isRequired,
  getRefundRecharge: PropTypes.func.isRequired,
  settlementBankDetails: PropTypes.object.isRequired,
  twoFactorAuthentication: PropTypes.any,
  resetTwoFactorAuthentication: PropTypes.func,
  resetRefundRecharge: PropTypes.func.isRequired,
  resetData: PropTypes.func.isRequired,
  verifyBankAccount: PropTypes.func.isRequired,
  cancelNewBankAccount: PropTypes.func.isRequired,
  createSelfServeTicket: PropTypes.func.isRequired,
};


const withConnect = connect(
  mapStateToProps,
  mapDispatchToProps,
);

const WithOnboardingContext = (props) => (
  <OnboardingProvider>
    <TwoFAProvider>
      <SettlementBankDetails {...props} />
    </TwoFAProvider>
  </OnboardingProvider>
)

export default compose(
  withConnect,
  memo,
)(WithOnboardingContext);